import { sha256 } from 'crypto-sha';
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { useAuth0 } from '@auth0/auth0-react';

import { sentryService } from '@/services/index';

import { PageProperties, useAnalytics } from '.';
import { useQueryParams } from '../../pages/purchaseJourney-layout/useQueryParams';

export const PageTracker = () => {
  const { isAuthenticated, user, isLoading } = useAuth0();
  const location = useLocation();
  const { trackPage } = useAnalytics();
  const userId = isAuthenticated ? user?.sub : undefined;
  const userEmail = user?.email;
  const userCountry = user?.['https://sky-common.co.nz/context']?.country_name;
  const userCity = user?.['https://sky-common.co.nz/context']?.city_name;

  const { refId } = useQueryParams();

  if (refId != null) {
    sessionStorage.setItem('refId', refId);
  }

  const [userEmailHashed, setUserEmailHashed] = React.useState<string | undefined>(undefined);
  const prevPathRef = useRef<string>('');

  // Hash email when available
  useEffect(() => {
    if (userEmail) {
      sha256(userEmail).then(hash => {
        setUserEmailHashed(hash);
      });
    } else {
      setUserEmailHashed(undefined);
    }
  }, [userEmail]);

  // Check if current URL should skip tracking
  function shouldSkipTracking(url: any) {
    const skipPatterns = ['/build-my-plan/checkout'];

    return skipPatterns.some(pattern => url.includes(pattern));
  }

  function pageTrackerEvent(title: string) {
    if (isLoading) return;
    if (prevPathRef.current === location.pathname) return;
    if (isAuthenticated && userEmail && !userEmailHashed) return; // wait still email is hashed

    const previousPath = (() => {
      if (prevPathRef.current && prevPathRef.current !== location.pathname) {
        return prevPathRef.current;
      }
      try {
        const referrerUrl = document.referrer ? new URL(document.referrer) : null;
        if (referrerUrl && referrerUrl.origin === window.location.origin) {
          return referrerUrl.pathname;
        }
      } catch (error) {
        sentryService.captureException(error, {
          extra: {
            message: 'PageTracker referrer parse error',
          },
        });
      }
      return null;
    })();

    const referrerValue =
      previousPath && previousPath !== location.pathname
        ? `${window.location.origin}${previousPath}`
        : document.referrer || null;

    let data: Partial<PageProperties> = {
      pageType:
        globalThis.location.pathname === '/'
          ? 'homepage'
          : globalThis.location.pathname.replace(/^\//, ''),
      isNewSite: true,
      isLoggedIn: isAuthenticated,
      userId,
      userCountry,
      userCity,
      userEmailHashed: userEmailHashed,
      path: window.location.pathname,
      url: window.location.href,
      title: title,
      search: window.location.search || null,
      referrer: referrerValue,
      userCheckoutId: sessionStorage.getItem('refId') ? sessionStorage.getItem('refId') : null,
      userResumedCheckout: sessionStorage.getItem('refId') ? true : false,
      // not providing these for now
      accountId: null,
      userPhoneHashed: null,
    };
    const currentUrl = window.location.pathname;
    if (shouldSkipTracking(currentUrl)) {
      return;
    }
    trackPage(data);
    prevPathRef.current = location.pathname;
  }
  return {
    pageTrackerEvent,
  };
};
